import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Building2, FileText, Loader2, LogOut, Network, Plus, Rss, Trash2 } from "lucide-react";
import ArticlesManager from "../components/admin/ArticlesManager";
import OrgManager from "../components/admin/OrgManager";
import AdminNetworks from "../components/admin/AdminNetworks";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "clymaco_admin_token";
const TAGS = ["deadline", "alert", "update", "notice"];

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg ? e.msg : String(e))).join(" ");
  return String(detail);
}

const emptyDraft = { tag: "update", text_en: "", text_hi: "", link: "", active: true };

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [stage, setStage] = useState(token ? "checking" : "login");
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [tab, setTab] = useState("ticker");

  const api = useCallback(
    async (path, options = {}) => {
      const resp = await fetch(`${API}/api${path}`, {
        ...options,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
      });
      if (resp.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setStage("login");
        throw new Error("Session expired. Please log in again.");
      }
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(formatApiError(data.detail));
      }
      return resp.json();
    },
    [token]
  );

  const loadItems = useCallback(async () => {
    const data = await api("/admin/ticker");
    setItems(data.items);
  }, [api]);

  useEffect(() => {
    if (!token) return;
    api("/auth/me")
      .then((u) => {
        setUser(u);
        setStage("dashboard");
        loadItems().catch(() => {});
      })
      .catch(() => {});
  }, [token, api, loadItems]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setLoginError("");
    try {
      const resp = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(formatApiError(data.detail));
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      setToken(data.token);
      setStage("dashboard");
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(null);
    setStage("login");
  };

  const addItem = async (e) => {
    e.preventDefault();
    try {
      await api("/admin/ticker", { method: "POST", body: JSON.stringify(draft) });
      setDraft(emptyDraft);
      await loadItems();
      toast.success("Ticker item added");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const saveItem = async (item) => {
    try {
      await api(`/admin/ticker/${item.id}`, { method: "PUT", body: JSON.stringify(item) });
      await loadItems();
      toast.success("Saved");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deleteItem = async (id) => {
    try {
      await api(`/admin/ticker/${id}`, { method: "DELETE" });
      await loadItems();
      toast.success("Deleted");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleActive = async (item, checked) => {
    patchItem(item.id, { active: checked });
    try {
      await api(`/admin/ticker/${item.id}`, { method: "PUT", body: JSON.stringify({ ...item, active: checked }) });
      toast.success(checked ? "Item is live" : "Item hidden");
    } catch (err) {
      toast.error(err.message);
      patchItem(item.id, { active: !checked });
    }
  };

  const move = async (index, dir) => {
    const next = [...items];
    const swap = index + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[index], next[swap]] = [next[swap], next[index]];
    setItems(next);
    try {
      await api("/admin/ticker-reorder", { method: "POST", body: JSON.stringify({ ids: next.map((i) => i.id) }) });
    } catch (err) {
      toast.error(err.message);
      loadItems().catch(() => {});
    }
  };

  const patchItem = (id, patch) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));

  const inputCls = "w-full rounded-lg border border-emerald-950/15 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-600";
  const selectCls = "rounded-lg border border-emerald-950/15 bg-white px-2.5 py-2 text-sm outline-none focus:border-emerald-600";

  if (stage === "checking") {
    return (
      <main className="flex min-h-[60vh] items-center justify-center bg-parchment-base pt-24" data-testid="admin-checking">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />
      </main>
    );
  }

  if (stage === "login") {
    return (
      <main className="noise-overlay relative flex min-h-[70vh] items-center justify-center bg-parchment-base px-4 pt-24 pb-16" data-testid="admin-login">
        <form onSubmit={submitLogin} className="w-full max-w-sm rounded-3xl border border-emerald-950/10 bg-white p-8 shadow-xl shadow-emerald-900/10" data-testid="admin-login-form">
          <img src="/logo-mark.png" alt="CLYMACO" className="h-9 w-auto" />
          <div className="mt-4 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-800/70">CLYMACO Admin</div>
          <h1 className="mt-2 font-display text-2xl font-bold text-forest-deep">Sign in</h1>
          <label className="mt-6 block text-sm font-semibold text-forest-deep/80">Email</label>
          <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-1`} autoComplete="username" />
          <label className="mt-4 block text-sm font-semibold text-forest-deep/80">Password</label>
          <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} mt-1`} autoComplete="current-password" />
          {loginError && (
            <p data-testid="admin-login-error" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
              {loginError}
            </p>
          )}
          <button data-testid="admin-login-submit" type="submit" disabled={busy} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-forest-primary py-3 font-display text-sm font-bold text-white transition-colors hover:bg-forest-rich disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign in
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="noise-overlay relative min-h-screen bg-parchment-base px-4 pt-28 pb-20 sm:px-6" data-testid="admin-dashboard">
      <div className={`mx-auto ${tab === "networks" ? "max-w-7xl" : "max-w-4xl"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-800/70">CLYMACO Admin</div>
            <h1 className="mt-1 font-display text-2xl font-bold text-forest-deep sm:text-3xl">Site controls</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-forest-deep/60" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-950/20 px-4 py-2 text-sm font-semibold text-forest-deep transition-colors hover:bg-emerald-900/5">
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>

        <div className="mt-6 flex gap-2" data-testid="admin-tabs">
          {[
            ["ticker", "EPR Wire ticker", Rss],
            ["articles", "Insights articles", FileText],
            ["orgs", "Organisations", Building2],
            ["networks", "Client networks", Network],
          ].map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-sm font-bold transition-colors ${tab === key ? "bg-forest-primary text-white" : "border border-emerald-950/15 text-forest-deep hover:bg-emerald-900/5"}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {tab === "articles" && <ArticlesManager api={api} />}
        {tab === "orgs" && <OrgManager api={api} />}
        {tab === "networks" && <AdminNetworks api={api} />}

        {tab === "ticker" && (
        <section className="mt-8" data-testid="ticker-manager">
          <div className="flex items-center gap-2">
            <Rss className="h-5 w-5 text-emerald-700" />
            <h2 className="font-display text-lg font-bold text-forest-deep">EPR Wire ticker</h2>
            <span className="rounded-full bg-emerald-900/5 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-emerald-800/70">{items.length} items</span>
          </div>
          <p className="mt-1 text-sm text-forest-deep/60">Runs under the hero on the homepage. Changes go live within seconds. Set an item inactive instead of deleting it if you may reuse it.</p>

          <form onSubmit={addItem} className="mt-5 rounded-2xl border-2 border-dashed border-emerald-950/15 bg-white/60 p-4" data-testid="ticker-add-form">
            <div className="grid gap-3 sm:grid-cols-[130px_1fr_1fr]">
              <select value={draft.tag} onChange={(e) => setDraft((d) => ({ ...d, tag: e.target.value }))} className={selectCls} data-testid="ticker-add-tag">
                {TAGS.map((tg) => (
                  <option key={tg} value={tg}>{tg.toUpperCase()}</option>
                ))}
              </select>
              <input required maxLength={300} placeholder="English text" value={draft.text_en} onChange={(e) => setDraft((d) => ({ ...d, text_en: e.target.value }))} className={inputCls} data-testid="ticker-add-text-en" />
              <input maxLength={300} placeholder="Hindi text (optional)" value={draft.text_hi} onChange={(e) => setDraft((d) => ({ ...d, text_hi: e.target.value }))} className={inputCls} data-testid="ticker-add-text-hi" />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <input placeholder="Link (optional, https:// or /practice/...)" value={draft.link} onChange={(e) => setDraft((d) => ({ ...d, link: e.target.value }))} className={`${inputCls} flex-1 min-w-[220px]`} data-testid="ticker-add-link" />
              <button type="submit" data-testid="ticker-add-submit" className="inline-flex items-center gap-1.5 rounded-full bg-forest-primary px-5 py-2.5 font-display text-sm font-bold text-white transition-colors hover:bg-forest-rich">
                <Plus className="h-4 w-4" /> Add item
              </button>
            </div>
          </form>

          <div className="mt-5 space-y-3">
            {items.map((item, idx) => (
              <div key={item.id} className={`rounded-2xl border border-emerald-950/10 bg-white p-4 shadow-sm ${item.active ? "" : "opacity-55"}`} data-testid={`ticker-row-${item.id}`}>
                <div className="grid gap-3 sm:grid-cols-[130px_1fr_1fr]">
                  <select value={item.tag} onChange={(e) => patchItem(item.id, { tag: e.target.value })} className={selectCls} data-testid={`ticker-tag-${item.id}`}>
                    {TAGS.map((tg) => (
                      <option key={tg} value={tg}>{tg.toUpperCase()}</option>
                    ))}
                  </select>
                  <input required maxLength={300} value={item.text_en} onChange={(e) => patchItem(item.id, { text_en: e.target.value })} className={inputCls} data-testid={`ticker-text-en-${item.id}`} />
                  <input maxLength={300} placeholder="Hindi text" value={item.text_hi} onChange={(e) => patchItem(item.id, { text_hi: e.target.value })} className={inputCls} data-testid={`ticker-text-hi-${item.id}`} />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input placeholder="Link (optional)" value={item.link} onChange={(e) => patchItem(item.id, { link: e.target.value })} className={`${inputCls} flex-1 min-w-[200px]`} data-testid={`ticker-link-${item.id}`} />
                  <label className="flex items-center gap-2 text-sm font-semibold text-forest-deep/75">
                    <input type="checkbox" checked={item.active} onChange={(e) => toggleActive(item, e.target.checked)} className="h-4 w-4 accent-emerald-700" data-testid={`ticker-active-${item.id}`} />
                    Active
                  </label>
                  <div className="ml-auto flex items-center gap-1.5">
                    <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} aria-label="Move up" className="rounded-full border border-emerald-950/15 p-2 text-forest-deep transition-colors hover:bg-emerald-900/5 disabled:opacity-30" data-testid={`ticker-up-${item.id}`}>
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} aria-label="Move down" className="rounded-full border border-emerald-950/15 p-2 text-forest-deep transition-colors hover:bg-emerald-900/5 disabled:opacity-30" data-testid={`ticker-down-${item.id}`}>
                      <ArrowDown className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => saveItem(item)} className="rounded-full bg-forest-primary px-4 py-2 font-display text-sm font-bold text-white transition-colors hover:bg-forest-rich" data-testid={`ticker-save-${item.id}`}>
                      Save
                    </button>
                    <button type="button" onClick={() => deleteItem(item.id)} aria-label="Delete" className="rounded-full border border-red-200 p-2 text-red-600 transition-colors hover:bg-red-50" data-testid={`ticker-delete-${item.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
        )}
      </div>
    </main>
  );
}
